import {
  useCallback,
  memo,
  MouseEvent,
  useState,
  useRef,
  useMemo,
  ChangeEvent,
  useEffect,
} from "react";
import { useAppDispatch, useAppSelector } from "stores";
import store from "stores/interfaces";
import {
  Avatar,
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import styled from "styled-components";
import sendEvent from "modules/sendEvent";
import MediumListOfficialPlaylistItemMenu from "./MediumListOfficialPlaylistItemMenu";
import { appStateMutate } from "stores/modules/app.state/operations";
import { Timestamp } from "firebase/firestore";
import { Cancel, Search } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import theme from "theme";
import { toHarfWidthAndUpperCase } from "modules/stringUtils";
import { uniqBy } from "lodash-es";

const MediumListOfficialPlaylist = () => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation();

  const [menuSoundId, setMenuSoundId] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState(false);
  const refAnchor = useRef<Element | null>(null);
  const onOpenMenu = useCallback((soundId: string, el: Element) => {
    refAnchor.current = el;
    setMenuSoundId(soundId);
    setOpenMenu(true);
  }, []);
  const onCloseMenu = useCallback(() => {
    setOpenMenu(false);
    window.setTimeout(() => {
      dispatch(
        appStateMutate((state) => {
          state.previewMediaUrl = null;
        })
      );
    }, 100);
  }, [dispatch]);

  const tab = useAppSelector((state) => {
    return store.getAppState(state, "userMediumListOfficialPlaylistTab");
  });
  const playlists = useAppSelector((state) => {
    return store.getAppState(state, "officialPlaylists");
  });
  const isPro = useAppSelector((state) => {
    return store.getAppState(state, "plan") === "ccfolia-pro";
  });

  const selectPlaylist = playlists.find((playlist) => playlist.id === tab);
  const targetPlaylists = useMemo(() => {
    return tab === "all" ? playlists : selectPlaylist ? [selectPlaylist] : [];
  }, [tab, playlists, selectPlaylist]);
  const primaryCategoriesBySoundId = useMemo(() => {
    const categoriesBySoundId = new Map<
      string,
      { name: string; priority: number }
    >();
    for (const playlist of playlists) {
      for (const sound of playlist.sounds) {
        const currentCategory = categoriesBySoundId.get(sound.id);
        if (
          currentCategory == null ||
          currentCategory.priority < playlist.priority
        ) {
          categoriesBySoundId.set(sound.id, {
            name: playlist.name,
            priority: playlist.priority,
          });
        }
      }
    }
    return categoriesBySoundId;
  }, [playlists]);
  const [search, setSearch] = useState("");

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const playListRef = useRef<HTMLDivElement>(null);

  const resetSearch = useCallback(() => {
    setSearch("");
  }, []);

  useEffect(() => {
    const ref = searchInputRef.current;
    if (!ref) return;

    if (ref.value !== search) {
      ref.value = search;
    }
  }, [search]);

  const filteredSounds = useMemo(() => {
    const now = Timestamp.now();
    const checkedAtMillis = now.toMillis() - 14 * 24 * 60 * 60 * 1000; // 2週間前までをisNewの対象に
    const parseIOSDateMillis = (date?: string) => {
      return date ? Date.parse(date) : 0;
    };

    let searchedPlaylists = targetPlaylists;
    if (search) {
      const searchText = toHarfWidthAndUpperCase(search);
      searchedPlaylists = targetPlaylists.map((playlist) => {
        let sounds = playlist.sounds;
        if (toHarfWidthAndUpperCase(playlist.name).indexOf(searchText) < 0) {
          sounds = playlist.sounds.filter(
            (sound) =>
              toHarfWidthAndUpperCase(sound.name).indexOf(searchText) > -1 ||
              toHarfWidthAndUpperCase(sound.provider).indexOf(searchText) > -1
          );
        }
        return {
          ...playlist,
          sounds,
        };
      });
    }
    let viewSounds = searchedPlaylists
      .map((playlist) => {
        const listDisabled = !playlist.free && !isPro;
        return playlist.sounds.map((sound) => {
          return {
            ...sound,
            disabled: listDisabled,
            playlistName: playlist.name,
            isNew: sound.addedAt
              ? parseIOSDateMillis(sound.addedAt) >= checkedAtMillis
              : false,
          };
        });
      })
      .flat();
    if (tab === "all") {
      // 「すべて」表示の場合は同一楽曲を1件にまとめ、最優先カテゴリ名を表示する
      viewSounds.sort(
        (a, b) => parseIOSDateMillis(b.addedAt) - parseIOSDateMillis(a.addedAt)
      );
      viewSounds = uniqBy(viewSounds, "id").map((sound) => ({
        ...sound,
        playlistName:
          primaryCategoriesBySoundId.get(sound.id)?.name ?? sound.playlistName,
      }));
    }
    return viewSounds;
  }, [targetPlaylists, search, isPro, tab, primaryCategoriesBySoundId]);

  useEffect(() => {
    const ref = playListRef.current;
    if (ref) {
      ref.scrollTop = 0;
    }
  }, [tab, search]);

  return (
    <>
      <Box position="static">
        <TextField
          fullWidth
          variant="filled"
          inputRef={searchInputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: (
              <IconButton
                onClick={resetSearch}
                sx={{ color: theme.palette.text.secondary }}
              >
                <Cancel />
              </IconButton>
            ),
          }}
          hiddenLabel
          onChange={onChange}
          placeholder={t("曲名や作者名、分類名で検索")}
        />
      </Box>
      <Container ref={playListRef}>
        <List component="div">
          {filteredSounds.map((sound) => (
            <div key={sound.id}>
              <MediumListOfficialPlaylistItem
                id={sound.id}
                name={sound.name}
                provider={sound.provider}
                url={sound.url}
                volume={sound.volume}
                disabled={sound.disabled}
                playlistName={sound.playlistName}
                isNew={sound.isNew}
                onOpenMenu={onOpenMenu}
              />
            </div>
          ))}
        </List>
        <MediumListOfficialPlaylistItemMenu
          open={openMenu}
          soundId={menuSoundId}
          anchorEl={refAnchor.current}
          onClose={onCloseMenu}
        />
      </Container>
    </>
  );
};

type MediumListOfficialPlaylistItemProps = {
  id: string;
  name: string;
  provider: string;
  url: string;
  volume: number;
  disabled?: boolean;
  playlistName: string;
  isNew: boolean;
  onOpenMenu: (soundId: string, el: Element) => void;
};

const MediumListOfficialPlaylistItem = ({
  id,
  name,
  provider,
  url,
  volume,
  disabled,
  playlistName,
  isNew,
  onOpenMenu,
}: MediumListOfficialPlaylistItemProps) => {
  const dispatch = useAppDispatch();
  const dir = useAppSelector((state) =>
    store.getAppState(state, "openRoomMediaDir")
  );
  const officialPlaylistVolume = useAppSelector((state) =>
    store.getAppState(state, "officialPlaylistVolume")
  );
  const officialPlaylistLoop = useAppSelector((state) =>
    store.getAppState(state, "officialPlaylistLoop")
  );

  const onPreview = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onOpenMenu(id, e.currentTarget);
    },
    [id, onOpenMenu]
  );

  const onPlay = useCallback(() => {
    if (url) {
      sendEvent("clickOfficialSound", {
        event_category: name,
        event_label: id,
      });
      if (dir === "bgm") {
        dispatch(
          store.updateCurrentRoomField({
            mediaUrl: url,
            mediaRef: null,
            mediaType: "file",
            mediaName: name,
            mediaVolume: volume * officialPlaylistVolume,
            mediaRepeat: officialPlaylistLoop,
          })
        );
      } else if (dir === "sound") {
        dispatch(
          store.updateCurrentRoomField({
            soundUrl: url,
            soundRef: null,
            soundName: name,
            soundVolume: volume * officialPlaylistVolume,
            soundRepeat: officialPlaylistLoop,
          })
        );
      } else if (dir === "effect") {
        dispatch(
          store.updateCurrentRoomEffect({
            soundUrl: url,
            soundRef: null,
            soundName: name,
            soundVolume: volume * officialPlaylistVolume,
          })
        );
      }
      // 試聴を停止
      dispatch(
        store.appStateMutate((state) => {
          state.previewMediaUrl = null;
        })
      );
    }
  }, [
    dispatch,
    id,
    url,
    name,
    volume,
    dir,
    officialPlaylistVolume,
    officialPlaylistLoop,
  ]);

  return (
    <ListItem
      component="div"
      secondaryAction={
        <IconButton onClick={onPreview} disabled={disabled}>
          <MoreVertIcon />
        </IconButton>
      }
      disablePadding
    >
      <ListItemButton
        disabled={disabled}
        style={{
          paddingRight: "100px",
        }}
        onClick={onPlay}
        sx={{
          paddingY: isNew ? "4px" : undefined,
        }}
      >
        <ListItemAvatar>
          <Avatar>
            <Avatar>
              <LibraryMusicIcon />
            </Avatar>
          </Avatar>
        </ListItemAvatar>
        <ListItemTextWrapped
          onClick={(e) => e.preventDefault()}
          primary={
            <>
              {isNew && (
                <Typography color="secondary" variant="body2">
                  New
                </Typography>
              )}
              <Typography>{name}</Typography>
            </>
          }
          secondary={`${provider} - ${playlistName}`}
          sx={{
            marginY: isNew ? 0 : undefined,
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

const ListItemTextWrapped = styled(ListItemText)`
  word-wrap: break-word;
`;

const Container = styled.div`
  padding: 0;
  flex: 1;
  overflow: auto;
`;

export default memo(MediumListOfficialPlaylist);
